import type { FC, SVGProps } from 'react';

import { iconMap } from './iconMap';

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  /**
   * Имя иконки в v4-нотации (как в `icon="plus"` у кнопок и `icon: 'truck'` в url.js).
   * Если имя есть в `iconMap` — рисуется inline-SVG (стабильно, без FOIT/FOUT).
   * Если нет — fallback на FA-шрифт `fa fa-<name>`.
   */
  name: string;
  /** Аналог `fa-spin` — собственная CSS-анимация `.icon-spin`, не зависит от FA-шрифта. */
  spin?: boolean;
}

/**
 * Единая SVG-иконка проекта.
 *
 * Источник path-данных — `@fortawesome/free-solid-svg-icons` (JS-объекты).
 * Рендерится как inline-SVG с `fill="currentColor"` и размером `1em` —
 * наследует цвет и размер текста, как шрифт FA, но без асинхронной загрузки.
 *
 * Многокомпонентные иконки (`icon[4]` — массив path'ов) рисуются несколькими `<path>`.
 */
const Icon: FC<IconProps> = ({ name, className, spin, style }) => {
  const faIcon = iconMap[name];

  // Fallback на FA-шрифт для ещё не заведённых имён — нулевая регрессия.
  if (!faIcon) {
    return <i className={ `fa fa-${ name } ${ className || '' }` } style={ style } />;
  }

  const [width, height, , , pathData] = faIcon.icon;
  const paths = Array.isArray(pathData) ? pathData : [pathData];

  return (
    <svg
      className={ `${ spin ? 'icon-spin ' : '' }${ className || '' }` }
      style={ { width: '1em', height: '1em', verticalAlign: '-0.125em', ...style } }
      viewBox={ `0 0 ${ width } ${ height }` }
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      { paths.map((d, i) => <path key={ i } d={ d } />) }
    </svg>
  );
};

export default Icon;
